import { useCallback } from 'react'
import { useAuth as useAuthContext } from '../auth'
import type { User } from 'oidc-client-ts'

/**
 * Enhanced authentication hook with session management
 * LEGACY: slu-frontend/src/common/security/security.js
 * LEGACY: slu-frontend/src/common/security/session.js
 * LEGACY: slu-frontend/src/common/security/login
 * LEGACY: slu-frontend/src/common/security/logout
 *
 * Provides authentication state and methods that replace the AngularJS
 * security, session, login, and logout services.
 */

export interface AuthUser extends User {
    userId?: string
    username?: string
    email?: string
    firstName?: string
    lastName?: string
    isActive?: boolean
    previousLoginDate?: string
    abilities?: string[]
    features?: string[]
}

export interface UseAuthReturn {
    // State
    user: AuthUser | null
    isAuthenticated: boolean
    isLoading: boolean
    currentUser: AuthUser | null

    // Authentication methods
    login: () => Promise<void>
    logout: (reason?: string) => Promise<void>
    getAccessToken: () => string | null

    // User management
    refreshCurrentUser: () => Promise<AuthUser | null>
    updateUser: (email: string, firstName: string, lastName: string) => void

    // Authorization
    isAuthorized: (ability: string) => boolean
    isFeatureEnabled: (feature: string) => boolean
    isAdmin: () => boolean
    isActive: () => boolean
}

/**
 * Hook that extends the base AuthContext with additional security features
 * from the legacy AngularJS security service.
 */
export function useAuth(): UseAuthReturn {
    const auth = useAuthContext()

    const currentUser = auth.user as AuthUser | null

    const refreshCurrentUser = useCallback(async (): Promise<AuthUser | null> => {
        // In React, the AuthContext automatically manages user state
        // This is a no-op since the context handles user refresh
        return currentUser
    }, [currentUser])

    const updateUser = useCallback(
        (email: string, firstName: string, lastName: string) => {
            // Update user info (this would typically be done via API and state update)
            if (currentUser) {
                currentUser.email = email
                currentUser.firstName = firstName
                currentUser.lastName = lastName
            }
        },
        [currentUser],
    )

    const isAuthorized = useCallback(
        (ability: string): boolean => {
            if (!currentUser?.abilities) return false
            return currentUser.abilities.includes(ability)
        },
        [currentUser],
    )

    const isFeatureEnabled = useCallback(
        (feature: string): boolean => {
            if (!currentUser?.features) return false
            return currentUser.features.includes(feature)
        },
        [currentUser],
    )

    const isAdmin = useCallback((): boolean => {
        return isAuthorized('SLU_Admin')
    }, [isAuthorized])

    const isActiveUser = useCallback((): boolean => {
        return !!currentUser && currentUser.isActive === true
    }, [currentUser])

    const logout = useCallback(
        async (reason?: string) => {
            // Optional: Log reason for logout (timeout, user action, etc.)
            if (reason) {
                console.info(`Logout reason: ${reason}`)
            }
            await auth.logout()
        },
        [auth],
    )

    return {
        // State
        user: currentUser,
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        currentUser,

        // Authentication methods
        login: auth.login,
        logout,
        getAccessToken: auth.getAccessToken,

        // User management
        refreshCurrentUser,
        updateUser,

        // Authorization
        isAuthorized,
        isFeatureEnabled,
        isAdmin,
        isActive: isActiveUser,
    }
}
